import {
  BookingStatus,
  LoadStatus,
  PurchaseLoadStatus,
  PurchaseOrderStatus,
  SalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { Badge, cn } from '@rematter/ui-web';
import { ThemeColor } from '@rematter/ui-web/types';
import { LucideArrowLeft } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  getBookingStatusColor,
  getBookingStatusText,
  getPurchaseLoadStatusColor,
  getPurchaseLoadStatusText,
  getPurchaseOrderStatusColor,
  getPurchaseOrderStatusText,
  getSalesOrderStatusColor,
  getSalesOrderStatusText,
  loadStatusTextMap,
  loadStatusThemeColors,
} from '~/modules/trade/utils/status';

type LoadStatusBadgeProps = {
  status?: LoadStatus;
  isReviewed?: boolean;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

// Base badge for all status types
interface BaseStatusBadgeProps {
  color: ThemeColor;
  text: string | undefined;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
}

const BaseStatusBadge = ({
  color,
  text,
  size = 'small',
  className,
}: BaseStatusBadgeProps) => (
  <Badge
    color={color}
    size={size}
    className={cn('@BaseStatusBadge', className)}
  >
    {text}
  </Badge>
);

type SalesOrderBadgeProps = {
  salesOrderNumber?: string;
  counterPartyPurchaseOrderNumber?: string;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const SalesOrderBadge = ({
  salesOrderNumber,
  counterPartyPurchaseOrderNumber,
  size = 'small',
  className,
}: SalesOrderBadgeProps) => (
  <Badge
    color="primary"
    size={size}
    className={cn(
      '@SalesOrderBadge',
      className,
      'flex flex-row items-center gap-2',
    )}
  >
    <div>{salesOrderNumber}</div>
    {counterPartyPurchaseOrderNumber && (
      <div className="flex flex-row items-center gap-2">
        <LucideArrowLeft className="size-3 text-primary opacity-50" />
        <div>{counterPartyPurchaseOrderNumber}</div>
      </div>
    )}
  </Badge>
);

type BookingBadgeProps = {
  bookingNumber?: string;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const BookingBadge = ({
  bookingNumber,
  size = 'small',
  className,
}: BookingBadgeProps) => (
  <Badge color="primary" size={size} className={cn('@BookingBadge', className)}>
    {bookingNumber}
  </Badge>
);

const LoadStatusBadge = ({
  status,
  isReviewed,
  size = 'small',
  className,
}: LoadStatusBadgeProps) => {
  const t = useTranslations();
  const color = status ? loadStatusThemeColors[status] : 'neutral';
  const text = status ? loadStatusTextMap[status](t, isReviewed) : '';

  return (
    <BaseStatusBadge
      color={color}
      text={text}
      size={size}
      className={cn('@LoadStatusBadge', className)}
    />
  );
};

type BookingStatusBadgeProps = {
  status?: BookingStatus;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const BookingStatusBadge = ({
  status,
  size = 'small',
  className,
}: BookingStatusBadgeProps) => {
  const t = useTranslations();
  const color = getBookingStatusColor(status);
  const text = getBookingStatusText({ status, t });
  return (
    <BaseStatusBadge
      color={color}
      text={text}
      size={size}
      className={cn('@BookingStatusBadge', className)}
    />
  );
};

type SalesOrderStatusBadgeProps = {
  status?: SalesOrderStatus;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const SalesOrderStatusBadge = ({
  status,
  size = 'small',
  className,
}: SalesOrderStatusBadgeProps) => {
  const t = useTranslations();
  const color = getSalesOrderStatusColor(status);
  const text = getSalesOrderStatusText({ status, t });
  return (
    <BaseStatusBadge
      color={color}
      text={text}
      size={size}
      className={cn('@SalesOrderStatusBadge', className)}
    />
  );
};

type PurchaseOrderStatusBadgeProps = {
  status?: PurchaseOrderStatus;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const PurchaseOrderStatusBadge = ({
  status,
  size = 'small',
  className,
}: PurchaseOrderStatusBadgeProps) => {
  const t = useTranslations();
  const color = getPurchaseOrderStatusColor(status);
  const text = getPurchaseOrderStatusText({ status, t });
  return (
    <BaseStatusBadge
      color={color}
      text={text}
      size={size}
      className={cn('@PurchaseOrderStatusBadge', className)}
    />
  );
};

type PurchaseLoadStatusBadgeProps = {
  status?: PurchaseLoadStatus;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const PurchaseLoadStatusBadge = ({
  status,
  size = 'small',
  className,
}: PurchaseLoadStatusBadgeProps) => {
  const t = useTranslations();
  const color = getPurchaseLoadStatusColor(status);
  const text = getPurchaseLoadStatusText({ status, t });
  return (
    <BaseStatusBadge
      color={color}
      text={text}
      size={size}
      className={cn('@PurchaseLoadStatusBadge', className)}
    />
  );
};

export {
  BookingBadge,
  BookingStatusBadge,
  LoadStatusBadge,
  PurchaseLoadStatusBadge,
  PurchaseOrderStatusBadge,
  SalesOrderBadge,
  SalesOrderStatusBadge,
};
